/** Operator dashboard for WaveScore Streams — shows every config the
 *  signed-in user owns with mount URL, bandwidth usage, listener history,
 *  and secret rotation. Lives at /app/streams (behind AppShell guard).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

const C = T.dark;

export default function AppStreams() {
  const [configs, setConfigs] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api.get("/streams/configs").then((r) => setConfigs(r.data.configs || []))
      .finally(() => setBusy(false));
  }, []);

  return (
    <div data-testid="app-streams" style={{ padding: 24, maxWidth: 1080 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
        marginBottom: 22, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: C.text }}>
            My hosted streams
          </h1>
          <p style={{ color: C.muted, margin: "6px 0 0", fontSize: 13 }}>
            Every stream WaveScorePro is hosting for you. Bandwidth resets 30 days
            after activation. Secrets can be rotated any time.
          </p>
        </div>
        <Link to="/streams" style={{
          background: T.greenAccent, color: "#0A1628",
          padding: "8px 18px", borderRadius: 999,
          fontSize: 13, fontWeight: 700, textDecoration: "none",
        }}>+ Apply for another stream</Link>
      </div>

      {busy && (
        <div data-testid="app-streams-loading" style={{ color: C.muted, fontSize: 14 }}>
          Loading…
        </div>
      )}
      {!busy && configs.length === 0 && (
        <div data-testid="app-streams-empty" style={{ padding: 22,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
          color: C.muted, fontSize: 14 }}>
          No hosted streams yet.{" "}
          <Link to="/streams" style={{ color: T.greenAccent }}>Apply for free hosting →</Link>
        </div>
      )}
      {configs.map((c) => <StreamCard key={c.id} config={c} />)}
    </div>
  );
}


function StreamCard({ config }) {
  const [cfg, setCfg] = useState(config);
  const [usage, setUsage] = useState([]);
  const [showSecrets, setShowSecrets] = useState(false);
  const [reveal, setReveal] = useState(null);   // one-shot revealed secrets
  const [busyAction, setBusyAction] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get(`/streams/configs/${cfg.id}/usage?hours=24`)
      .then((r) => setUsage(r.data.samples || []))
      .catch(() => {});
  }, [cfg.id]);

  const bytesInPeriod = cfg.bandwidth_bytes_this_period || 0;
  const gbUsed = bytesInPeriod / 1024 ** 3;
  const gbCap = cfg.bandwidth_gb_cap || 50;
  const pct = Math.min(100, Math.round((gbUsed / gbCap) * 100));
  const listenersNow = cfg.last_telemetry_listeners || 0;

  const reveal_secrets = async () => {
    const pw = window.prompt("Confirm your account password to reveal secrets:");
    if (!pw) return;
    setBusyAction("reveal");
    try {
      const { data } = await api.get(`/streams/configs/${cfg.id}?reveal=1`, {
        headers: { "X-Confirm-Password": pw },
      });
      setReveal(data.config);
      setShowSecrets(true);
      setMsg({ tone: "ok", text: "Secrets revealed for this session — do not share." });
    } catch (e) {
      setMsg({ tone: "err", text: e.response?.data?.detail || "Password did not match." });
    } finally { setBusyAction(null); }
  };

  const rotate_secret = async () => {
    if (!window.confirm("Rotating will invalidate the current telemetry secret. Your Icecast box must be updated with the new value. Continue?")) return;
    const pw = window.prompt("Confirm your account password:");
    if (!pw) return;
    setBusyAction("rotate");
    try {
      const { data } = await api.post(`/streams/configs/${cfg.id}/rotate-secret`, {}, {
        headers: { "X-Confirm-Password": pw },
      });
      setReveal({ ...(reveal || cfg), telemetry_secret: data.telemetry_secret });
      setShowSecrets(true);
      setMsg({ tone: "ok",
        text: "New telemetry secret generated. Copy it now — it won't be shown again." });
      // Refresh the masked copy in cfg too.
      const { data: fresh } = await api.get(`/streams/configs/${cfg.id}`);
      setCfg(fresh.config);
    } catch (e) {
      setMsg({ tone: "err", text: e.response?.data?.detail || "Rotation failed." });
    } finally { setBusyAction(null); }
  };

  return (
    <div data-testid={`stream-card-${cfg.id}`} style={{ marginBottom: 22, padding: 22,
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start",
        gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: C.text, fontSize: 18, fontWeight: 700 }}>{cfg.station_name}</div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
            Mount slug: <code style={{ color: T.greenAccent }}>{cfg.mount_slug}</code> ·
            {" "}Since {new Date(cfg.activated_at).toLocaleDateString()}
          </div>
        </div>
        <StatusPill status={cfg.status} />
      </div>

      {/* Bandwidth bar */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted, marginBottom: 6 }}>
          <span>Bandwidth this period</span>
          <span data-testid={`stream-bw-${cfg.id}`}>
            {gbUsed.toFixed(2)} / {gbCap} GB · {pct}%
          </span>
        </div>
        <div style={{ height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%",
            background: pct >= 100 ? "#F87171" : pct >= 80 ? "#F59E0B" : T.greenAccent,
            transition: "width 400ms ease" }} />
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12,
        fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, marginBottom: 18 }}>
        <Cell label="Listeners now" value={listenersNow} testId={`stream-listeners-${cfg.id}`} />
        <Cell label="Listener cap" value={cfg.listeners_cap} />
        <Cell label="Samples (24h)" value={usage.length} />
      </div>

      {/* Config values */}
      <div style={{ marginBottom: 18 }}>
        <KV k="Mount URL" v={<code>{cfg.mount_url}</code>} />
        <KV k="Source password" v={showSecrets && reveal ? <code>{reveal.source_password}</code> :
                                    <code>{cfg.source_password}</code>} />
        <KV k="Telemetry secret" v={showSecrets && reveal ? <code>{reveal.telemetry_secret}</code> :
                                     <code>{cfg.telemetry_secret}</code>} />
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {!showSecrets && (
          <button data-testid={`stream-reveal-${cfg.id}`} onClick={reveal_secrets}
            disabled={busyAction === "reveal"}
            style={btnPrimary}>
            {busyAction === "reveal" ? "…" : "Reveal secrets"}
          </button>
        )}
        <button data-testid={`stream-rotate-${cfg.id}`} onClick={rotate_secret}
          disabled={busyAction === "rotate"}
          style={btnGhost}>
          {busyAction === "rotate" ? "…" : "Rotate telemetry secret"}
        </button>
        <Link to="/crawler" style={{ ...btnGhost, textDecoration: "none",
          display: "inline-flex", alignItems: "center" }}>
          View crawler policy
        </Link>
      </div>

      {msg && (
        <div data-testid={`stream-msg-${cfg.id}`} style={{
          marginTop: 14, padding: 10, borderRadius: 8, fontSize: 12,
          background: msg.tone === "ok" ? "rgba(0,200,150,0.14)" : "rgba(248,113,113,0.14)",
          border: `1px solid ${msg.tone === "ok" ? "rgba(0,200,150,0.4)" : "rgba(248,113,113,0.4)"}`,
          color: msg.tone === "ok" ? T.greenAccent : "#F87171",
        }}>{msg.text}</div>
      )}
    </div>
  );
}


const KV = ({ k, v }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12,
    padding: "8px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
    <span style={{ color: C.muted }}>{k}</span>
    <span style={{ color: C.text, fontFamily: "'IBM Plex Mono',monospace",
      textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 460 }}>{v}</span>
  </div>
);

const Cell = ({ label, value, testId }) => (
  <div style={{ padding: "12px 14px", background: "rgba(255,255,255,0.03)",
    border: `1px solid ${C.border}`, borderRadius: 8 }}>
    <div style={{ color: C.muted, fontSize: 11 }}>{label}</div>
    <div data-testid={testId} style={{ color: C.text, fontSize: 20, fontWeight: 700 }}>
      {typeof value === "number" ? value.toLocaleString() : value}
    </div>
  </div>
);

const btnPrimary = { background: T.greenAccent, color: "#0A1628", border: "none",
  padding: "8px 16px", borderRadius: 999, cursor: "pointer",
  fontSize: 13, fontWeight: 700 };
const btnGhost = { background: "transparent", color: T.dark.text,
  border: `1px solid ${T.dark.border}`,
  padding: "8px 16px", borderRadius: 999, cursor: "pointer",
  fontSize: 13, fontWeight: 600 };

const STATUS_META = {
  active:      { color: "#34D399", label: "Active" },
  throttled:   { color: "#F59E0B", label: "Throttled" },
  suspended:   { color: "#94A3B8", label: "Suspended" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { color: "#94A3B8", label: status };
  return (
    <span style={{
      display: "inline-flex", padding: "5px 12px", borderRadius: 999,
      background: `${m.color}22`, border: `1px solid ${m.color}44`,
      color: m.color, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
      textTransform: "uppercase",
    }}>{m.label}</span>
  );
}
